import {
  CreateBankInvestmentTransferErrorType,
  useCreateBankInvestmentTransfer,
  type CreateBankInvestmentTransferInput,
  type CreateBankInvestmentTransferOutput,
} from '@egw/api'
import { type GQLError } from '@egw/clients'
import { useTranslation } from '@egw/i18n'
import { Logger } from '@egw/logger'
import { Currency } from '@egw/utils'
import { type UseFormSetError } from 'react-hook-form'
import { AMOUNT_KEY } from '../constants'
import {
  type BackendError,
  type EBackendError,
  type IGQLErrorResponse,
  type TransferFormInterface,
  type TransferSuccessTypes,
} from '../types'
import { getTransferSuccessType, handleGenericBackendErrors } from '../utils'

const log = new Logger('useBankInvestmentTransfer Hook')

interface UseBankInvestmentTransferInterface {
  onSuccess: (_successType: TransferSuccessTypes) => void
  onError: (_errorType: CreateBankInvestmentTransferErrorType | EBackendError) => void
  onInlineError: UseFormSetError<TransferFormInterface>
}

export const useBankInvestmentTransfer = ({
  onInlineError,
  onSuccess,
  onError,
}: UseBankInvestmentTransferInterface) => {
  const { t } = useTranslation('features-transfer')

  function onBankInvestmentTransferSuccess(
    data: CreateBankInvestmentTransferOutput,
    variables?: CreateBankInvestmentTransferInput,
  ): void {
    const createBankInvestmentTransfer = data

    // ERROR SCENARIO
    if (
      createBankInvestmentTransfer.__typename === 'CreateBankInvestmentTransferError' ||
      createBankInvestmentTransfer.__typename === 'InvestmentMinimumDepositNotMetError' ||
      createBankInvestmentTransfer.__typename === 'AnnualRetirementContributionExceededError'
    ) {
      let errorType:
        | CreateBankInvestmentTransferErrorType
        | 'AnnualRetirementContributionExceededError'
        | 'InvestmentMinimumDepositNotMetError'
        | undefined

      if (createBankInvestmentTransfer.__typename === 'CreateBankInvestmentTransferError') {
        errorType = createBankInvestmentTransfer.errorType
      } else if (createBankInvestmentTransfer.__typename === 'InvestmentMinimumDepositNotMetError') {
        errorType = 'InvestmentMinimumDepositNotMetError' // This custom error type should not be reached because the rule is stated in the form validation
      } else {
        errorType = 'AnnualRetirementContributionExceededError'
      }

      if (!errorType) return

      switch (errorType) {
        case CreateBankInvestmentTransferErrorType.INSUFFICIENT_FUNDS:
          onInlineError(AMOUNT_KEY, { message: t(`transfer.bank_investment_transfer_error.${errorType}`) }) // Sets an inline error on the amount key
          break
        case CreateBankInvestmentTransferErrorType.MINIMUM_DEPOSIT_NOT_MET:
          onInlineError(AMOUNT_KEY, { message: t(`transfer.bank_investment_transfer_error.${errorType}`) }) // Sets an inline error on the amount key
          break
        case 'InvestmentMinimumDepositNotMetError': // This custom error type should not be reached because the rule is stated in the form validation and would prevent user from submitting
          data.minimumDepositRequirementAmount &&
            onInlineError(AMOUNT_KEY, {
              message: t(`transfer.bank_investment_transfer_error.${errorType}`, {
                amount: Currency.integerCents.onlyMinus(data.minimumDepositRequirementAmount),
              }),
            }) // Sets an inline error on the amount key
          break
        case 'AnnualRetirementContributionExceededError':
          data.retirementContribution?.annualContributionsLimit &&
            onInlineError(AMOUNT_KEY, {
              message: t(`transfer.bank_investment_transfer_error.${errorType}`, {
                annualContributionLimit: Currency.integerCents.onlyMinus(
                  data.retirementContribution.annualContributionsLimit,
                ),
              }),
            }) // Sets an inline error on the amount key
          break
        case CreateBankInvestmentTransferErrorType.PENDING_TRANSFER_ALREADY_EXISTS:
          onError(errorType) // Sets a page error and will launch modal w/ the erorr content
          break
        case CreateBankInvestmentTransferErrorType.BAD_REQUEST:
          onError(errorType) // Sets a page error and will launch modal w/ the erorr content
          break
        case CreateBankInvestmentTransferErrorType.AMOUNT_REQUESTED_REQUIRES_LIQUIDATION:
          onError(errorType) // Sets a page error and will launch modal w/ the erorr content
          break
        default:
          log.error('Error Type not being handled', { errorType })
          break
      }
    }
    // SUCCESS SCENARIO
    if (createBankInvestmentTransfer.__typename === 'CreateBankInvestmentTransfer') {
      const transferSuccessType = getTransferSuccessType(createBankInvestmentTransfer, variables)
      onSuccess(transferSuccessType)
    } else {
      // Unhandled Fallback Scenarios
      log.warn('Unhandled Transfer Scenario')
    }
  }

  function onBankInvestmentTransferError(backendError: IGQLErrorResponse | BackendError[] | GQLError): void {
    const errorType = handleGenericBackendErrors({
      backendError,
    })

    onError(errorType) // Sets a page error and will launch modal w/ the erorr content
  }

  const { mutate: createBankInvestmentTransferMutation, isLoading: bankInvestmentTransferLoading } =
    useCreateBankInvestmentTransfer({
      onSuccess: onBankInvestmentTransferSuccess,
      onError: onBankInvestmentTransferError,
    })

  return {
    createBankInvestmentTransferMutation,
    bankInvestmentTransferLoading,
  }
}
